import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  Spin,
  Tabs,
  Button,
  Popover,
  Table,
  Tag,
  message as antMessage
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { downloadGroupStatementExcel } from '../../../utils/generateGroupExcel';
import useAuthContext from '../../../contexts/AuthContext';
import useErrorMessage from '../../../utils/ErrorMessage';
import PageHeader from '../../../components/PageHeader/PageHeader';
import ContentCustom from '../../../components/ContentCustom/ContentCustom';

const { TabPane } = Tabs;

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const MyGroup = () => {
  const { t } = useTranslation();
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const [group, setGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [downloading, setDownloading] = useState(null);

  const insurer = user?.insurer;

  const isGroupHead =
    insurer?.group &&
    insurer.group.head_insurer &&
    String(insurer.group.head_insurer._id || insurer.group.head_insurer) === String(insurer._id);

  const getGroup = async () => {
    if (!insurer?.group || !isGroupHead) return;
    setIsLoading(true);
    try {
      const groupId = insurer.group._id || insurer.group;
      const { data } = await dispatchAPI('GET', {
        url: `/groups/${groupId}`
      });
      setGroup(data);

      const membershipDates = data.members.map((m) => moment(m.membership_date));
      if (membershipDates.length) {
        const earliest = moment.min(membershipDates);
        const now = moment().subtract(1, 'month');
        const periods = [];
        const cursor = earliest.clone().startOf('month');
        while (cursor.isSameOrBefore(now, 'month')) {
          periods.push({ month: cursor.month() + 1, year: cursor.year() });
          cursor.add(1, 'month');
        }
        setAvailableMonths(periods);
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (insurer?._id) getGroup();
    // eslint-disable-next-line
  }, [insurer?._id]);

  const downloadStatement = async (month, year) => {
    const key = `${month}-${year}`;
    setDownloading(key);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/groups/${group._id}/commission-statement?month=${month}&year=${year}`
      });
      downloadGroupStatementExcel(data);
      antMessage.success(t('export.messages.success'));
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setDownloading(null);
  };

  const memberColumns = [
    {
      title: t('groups.form.member_name'),
      dataIndex: ['insurer', 'name'],
      key: 'name'
    },
    {
      title: t('groups.form.membership_date'),
      dataIndex: 'membership_date',
      key: 'membership_date',
      render: (d) => (d ? moment(d).format('MM/YYYY') : '-')
    },
    {
      title: t('groups.form.status'),
      key: 'status',
      render: (_, record) => {
        if (!record.end_membership_date)
          return <Tag color="green">{t('groups.my_group.status_active')}</Tag>;
        const isPast = moment(record.end_membership_date).isSameOrBefore(
          moment(),
          'month'
        );
        return isPast ? (
          <Tag color="default">
            {t('groups.my_group.status_ended', {
              date: moment(record.end_membership_date).format('MM/YYYY')
            })}
          </Tag>
        ) : (
          <Tag color="orange">
            {t('groups.my_group.status_scheduled', {
              date: moment(record.end_membership_date).format('MM/YYYY')
            })}
          </Tag>
        );
      }
    }
  ];

  const uniqueYears = [...new Set(availableMonths.map((p) => p.year))].sort();

  if (!insurer?.group) {
    return (
      <>
        <PageHeader title={t('groups.my_group.title')} />
        <ContentCustom>
          <p>{t('groups.my_group.no_group')}</p>
        </ContentCustom>
      </>
    );
  }

  if (!isGroupHead) {
    return (
      <>
        <PageHeader title={t('groups.my_group.title')} />
        <ContentCustom>
          <div style={{ marginBottom: 16 }}>
            <strong>{t('groups.form.name')}: </strong>
            {insurer.group?.name || '-'}
          </div>
          <p>{t('groups.my_group.not_head')}</p>
        </ContentCustom>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('groups.my_group.title')} />
      <ContentCustom>
        {isLoading ? (
          <Spin />
        ) : (
          <>
            {group && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <strong>{t('groups.form.name')}: </strong>
                  {group.name}
                </div>

                <div style={{ marginBottom: 24 }}>
                  <strong>{t('groups.form.members')}</strong>
                  <Table
                    style={{ marginTop: 8 }}
                    dataSource={group.members}
                    columns={memberColumns}
                    rowKey={(r) => String(r.insurer?._id || r.insurer)}
                    pagination={false}
                    size="small"
                  />
                </div>

                {availableMonths.length > 0 && (
                  <div>
                    <strong>{t('groups.my_group.statement_title')}</strong>
                    <Tabs type="card" style={{ marginTop: 12 }}>
                      {uniqueYears.map((year) => (
                        <TabPane tab={year} key={year}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                            {availableMonths
                              .filter((p) => p.year === year)
                              .map((p) => (
                                <Popover
                                  key={`${p.month}-${p.year}`}
                                  content={
                                    <Button
                                      icon={<DownloadOutlined />}
                                      loading={downloading === `${p.month}-${p.year}`}
                                      onClick={() => downloadStatement(p.month, p.year)}
                                    >
                                      Excel
                                    </Button>
                                  }
                                  trigger="click"
                                  placement="bottom"
                                >
                                  <Button>
                                    <DownloadOutlined />
                                    {MONTHS[p.month - 1]}
                                  </Button>
                                </Popover>
                              ))}
                          </div>
                        </TabPane>
                      ))}
                    </Tabs>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </ContentCustom>
    </>
  );
};

export default MyGroup;
